import 'react-quill/dist/quill.snow.css'

import React, { useRef } from 'react'
import { Button, Divider, H3, Icon, makeStyles } from '@perk-ui/core'
import { Field, FieldProps, Form, Formik } from 'formik'
import { X } from 'react-feather'
import ReactQuill from 'react-quill'
import * as Yup from 'yup'

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(2),
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
  quillEditor: {
    // Style the toolbar: round its top corners
    '& .ql-toolbar': {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
    },
    // Style the editor: round its bottom corners
    '& .ql-editor': {
      minHeight: '400px',
      maxHeight: '400px',
    },
    '& .ql-container': {
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
  },
}))

// Validation schema for the note content.
const NoteSchema = Yup.object().shape({
  noteContent: Yup.string().required('Note content is required'),
})

export interface AddNoteModalProps {
  initialContent?: string
  toggle: () => void
  onSave: (noteContent: string) => Promise<void>
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({
  initialContent,
  toggle,
  onSave,
}) => {
  const classes = useStyles()
  const quillRef = useRef<ReactQuill>(null)
  const titleText = initialContent ? 'Edit Clinical Note' : 'Add Clinical Note'

  return (
    <>
      <div className={classes.titleRow}>
        <H3>{titleText}</H3>
        <Button size="small" variant="text" onClick={() => toggle()}>
          <Icon size="medium" color="secondaryText">
            <X />
          </Icon>
        </Button>
      </div>
      <Divider className={classes.divider} />
      <Formik
        initialValues={{ noteContent: initialContent || '' }}
        validationSchema={NoteSchema}
        onSubmit={async (values, actions) => {
          try {
            await onSave(values.noteContent)
            toggle()
          } catch (error) {
            console.error('Error saving note:', error)
          } finally {
            actions.setSubmitting(false)
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Field name="noteContent">
              {({ field, form }: FieldProps) => (
                <ReactQuill
                  ref={quillRef}
                  value={field.value}
                  onChange={(content) =>
                    form.setFieldValue(field.name, content)
                  }
                  onBlur={() => form.setFieldTouched(field.name, true)}
                  className={classes.quillEditor}
                />
              )}
            </Field>
            <div className={classes.buttonContainer}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="medium"
                disabled={isSubmitting}
              >
                Update
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default AddNoteModal
